import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import StatusBadge from '../../components/StatusBadge';
import { useAuth } from '../../hooks/useAuth';
import { ROLES } from '../../utils/roles';
import * as payoutsApi from '../../api/payouts.api';
import { errorMessage } from '../../api/client';
import { formatMoney, formatDateTime } from '../../utils/format';

const STATUS_TONE = {
  PENDING:    'warning',
  APPROVED:   'info',
  PROCESSING: 'brand',
  PAID:       'success',
  FAILED:     'danger',
  REJECTED:   'danger',
  CANCELLED:  'neutral',
};

function Row({ label, children }) {
  return (
    <div className="row" style={{ padding: '6px 0', borderBottom: '1px solid var(--border)' }}>
      <div className="muted" style={{ minWidth: 160 }}>{label}</div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function PayoutDetails() {
  const { id } = useParams();
  const { user } = useAuth();
  const isAdmin = [ROLES.SUPER_ADMIN, ROLES.ADMIN].includes(user?.role);
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const p = isAdmin
          ? await payoutsApi.getAdminPayout(id)
          : await payoutsApi.getMyPayout(id);
        if (!cancelled) setDoc(p);
      } catch (e) {
        if (!cancelled) setErr(errorMessage(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id, isAdmin]);

  if (loading) return <LoadingSpinner />;
  if (err) return <div className="notice warning">{err}</div>;
  if (!doc) return <div className="notice">Not found.</div>;

  const back = isAdmin ? '/admin/payouts' : '/my-payouts';

  return (
    <>
      <PageHeader
        title={`Payout ${doc._id.slice(-8).toUpperCase()}`}
        subtitle={(() => {
          const who = doc.clinic?.clinicProfile?.clinicName
                   || doc.bankSnapshot?.accountHolderName
                   || doc.clinic?.name
                   || 'clinic';
          return `Payout to ${who} from a completed customer booking.`;
        })()}
        actions={<Link to={back} className="btn btn-ghost"><ArrowLeft size={16} /> Back</Link>}
      />

      <div className="grid grid-2" style={{ gap: 'var(--s-4)' }}>
        <div className="card">
          <h3 className="card-title">Money</h3>
          <Row label="Status"><StatusBadge tone={STATUS_TONE[doc.status] || 'neutral'}>{doc.status}</StatusBadge></Row>
          <Row label="Customer paid"><strong>{formatMoney(doc.grossAmount, doc.currency)}</strong></Row>
          <Row label="Clinic share">{doc.clinicSharePct}%</Row>
          <Row label="To clinic"><strong>{formatMoney(doc.clinicAmount, doc.currency)}</strong></Row>
          <Row label="Platform keeps">{formatMoney(doc.platformAmount, doc.currency)}</Row>
          <Row label="Currency">{doc.currency}</Row>
        </div>

        <div className="card">
          <h3 className="card-title">Timing</h3>
          <Row label="Created">{formatDateTime(doc.createdAt)}</Row>
          <Row label="Mode">{doc.createMode}</Row>
          <Row label="Released at">{doc.releasedAt ? formatDateTime(doc.releasedAt) : '—'}</Row>
          <Row label="Paid at">{doc.paidAt ? formatDateTime(doc.paidAt) : '—'}</Row>
          <Row label="Failed at">{doc.failedAt ? formatDateTime(doc.failedAt) : '—'}</Row>
          <Row label="Rejected at">{doc.rejectedAt ? formatDateTime(doc.rejectedAt) : '—'}</Row>
          {doc.rejectReason && <Row label="Reject reason">{doc.rejectReason}</Row>}
        </div>

        <div className="card">
          <h3 className="card-title">Order</h3>
          <Row label="Order number">
            {doc.order?._id
              ? <Link className="cell-mono" to={`/orders/${doc.order._id}`}>{doc.order.orderNumber}</Link>
              : '—'}
          </Row>
          <Row label="Booking id">{doc.order?.bookingId || '—'}</Row>
          <Row label="Package">{doc.order?.packageSnapshot?.name || '—'}</Row>
          <Row label="Order amount">{formatMoney(doc.order?.amount, doc.order?.currency || doc.currency)}</Row>
          <Row label="Completed at">{doc.order?.completedAt ? formatDateTime(doc.order.completedAt) : '—'}</Row>
          {isAdmin && doc.customer && (
            <>
              <Row label="Customer">{doc.customer.name || doc.customer.email || '—'}</Row>
              <Row label="Customer phone">{doc.customer.phone || '—'}</Row>
            </>
          )}
        </div>

        <div className="card">
          <h3 className="card-title">Bank details sent</h3>
          <Row label="Bank code">{doc.bankSnapshot?.bankCode || '—'}</Row>
          <Row label="Account number">{doc.bankSnapshot?.accountNumber || '—'}</Row>
          <Row label="Account holder">{doc.bankSnapshot?.accountHolderName || '—'}</Row>
          <Row label="Payment method">{doc.bankSnapshot?.paymentMethod === '2' ? 'DuitNow' : 'Instant Transfer'}</Row>
          {doc.bankSnapshot?.idType && <Row label="ID">{doc.bankSnapshot.idType} · {doc.bankSnapshot.idValue}</Row>}
          <Row label="Curlec batch">{doc.curlecBatchId || '—'}</Row>
          <Row label="Curlec payout id">{doc.curlecPayoutId || '—'}</Row>
          {doc.curlecStatusReason && <Row label="Curlec reason">{doc.curlecStatusReason}</Row>}
        </div>
      </div>

      {doc.note && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }}>
          <h3 className="card-title">Notes</h3>
          <div>{doc.note}</div>
        </div>
      )}

      {isAdmin && doc.webhookLogs?.length > 0 && (
        <div className="card" style={{ marginTop: 'var(--s-4)' }}>
          <h3 className="card-title">Curlec callback history ({doc.webhookLogs.length})</h3>
          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            {doc.webhookLogs.map((w, i) => (
              <pre key={i} className="text-xs" style={{ margin: '8px 0', padding: 8, background: 'var(--surface)', borderRadius: 6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                {formatDateTime(w.receivedAt)}{'\n'}
                {JSON.stringify(w.payload, null, 2)}
              </pre>
            ))}
          </div>
        </div>
      )}
    </>
  );
}

export default PayoutDetails;
